import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Plus, Pencil, Trash2, Search, MoreHorizontal, Check, X, Clock, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  confirme: { label: 'Confirmé', color: 'bg-emerald-100 text-emerald-800' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
  termine: { label: 'Terminé', color: 'bg-blue-100 text-blue-800' },
  expire: { label: 'Expiré', color: 'bg-gray-100 text-gray-600' },
};

export function ReservationTable({
  reservations,
  filteredReservations,
  paginatedReservations,
  rooms,
  loading,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  roomFilter,
  setRoomFilter,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalPages,
  isUser,
  isAdmin,
  isGestionnaire,
  onEdit,
  onDelete,
  onStatusChange,
  onNew,
}) {
  return (
    <div className="p-8 space-y-6" data-testid="reservations-content">
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="reservation-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="confirme">Confirmé</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="expire">Expiré</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roomFilter} onValueChange={setRoomFilter}>
              <SelectTrigger className="w-[180px]" data-testid="room-filter">
                <SelectValue placeholder="Salle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les salles</SelectItem>
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500">Lignes :</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(v === 'all' ? 'all' : Number(v))}>
                <SelectTrigger className="h-9 w-20" data-testid="reservation-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="60">60</SelectItem>
                  <SelectItem value="120">120</SelectItem>
                  <SelectItem value="all">Tous</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">
                {filteredReservations.length} / {reservations.length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reservations Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" style={{ width: 40, height: 40 }} />
        </div>
      ) : filteredReservations.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">
              {reservations.length === 0 ? 'Aucune réservation' : 'Aucun résultat'}
            </p>
            {reservations.length === 0 && (
              <>
                <p className="text-slate-400 mb-4">Créez votre première réservation</p>
                <Button onClick={onNew} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
                  <Plus className="mr-2 h-4 w-4" />
                  Nouvelle réservation
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titre</TableHead>
                <TableHead>Salle</TableHead>
                {!isUser && <TableHead>Bénéficiaire</TableHead>}
                <TableHead>Date & Heure</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedReservations.map((res) => (
                <TableRow 
                  key={res.id} 
                  className="table-row-hover"
                  data-testid={`reservation-row-${res.id}`}
                >
                  <TableCell className="font-medium">{res.title}</TableCell>
                  <TableCell>{res.room_name}</TableCell>
                  {!isUser && <TableCell>{res.client_name}</TableCell>}
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm">
                        {format(parseISO(res.start_datetime), 'd MMM yyyy', { locale: fr })}
                      </span>
                      <span className="text-xs text-slate-500">
                        {format(parseISO(res.start_datetime), 'HH:mm')} - {format(parseISO(res.end_datetime), 'HH:mm')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusConfig[res.status]?.color}>
                      {statusConfig[res.status]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`reservation-actions-${res.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!isUser && (
                          <DropdownMenuItem onClick={() => onEdit(res)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Modifier
                          </DropdownMenuItem>
                        )}
                        {(isAdmin || isGestionnaire) && res.status === 'en_attente' && (
                          <DropdownMenuItem onClick={() => onStatusChange(res, 'confirme')}>
                            <Check className="mr-2 h-4 w-4 text-emerald-600" />
                            Confirmer
                          </DropdownMenuItem>
                        )}
                        {isAdmin && res.status !== 'annule' && res.status !== 'termine' && (
                          <DropdownMenuItem onClick={() => onStatusChange(res, 'annule')}>
                            <X className="mr-2 h-4 w-4 text-red-600" />
                            Annuler
                          </DropdownMenuItem>
                        )}
                        {(isAdmin || isGestionnaire) && res.status === 'confirme' && (
                          <DropdownMenuItem onClick={() => onStatusChange(res, 'termine')}>
                            <Clock className="mr-2 h-4 w-4 text-blue-600" />
                            Terminer
                          </DropdownMenuItem>
                        )}
                        {isAdmin && res.status === 'annule' && (
                          <DropdownMenuItem onClick={() => onStatusChange(res, 'en_attente')}>
                            <Check className="mr-2 h-4 w-4 text-amber-600" />
                            Restaurer
                          </DropdownMenuItem>
                        )}
                        {!isUser && (
                          <DropdownMenuItem 
                            onClick={() => onDelete(res)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {/* Pagination */}
      {filteredReservations.length > 0 && pageSize !== 'all' && (
        <div className="flex items-center justify-between mt-4 px-2">
          <p className="text-sm text-slate-500">
            Page {currentPage} sur {totalPages} ({paginatedReservations.length} affichés sur {filteredReservations.length})
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ««
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Précédent
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Suivant
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              »»
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
